
import { difference } from "ramda";
import { mapGetters, mapState } from "vuex";

export default {
  inject: { uid: { default: "" } },

  props: { banners: { type: Array, default: () => [] } },

  data: () => ({
    topBarHeight: 100,
    isPageLoaded: false,
    restoreScrollKey: null,
    restoreScrollData: null,
  }),

  computed: {
    ...mapState("productsSearch", [
      "page",
      "currentPageResults",
      "loading",
      "cachedProductsKey",
      "restoredFromCache",
    ]),
    ...mapGetters("productsSearch", ["hits", "isFirstPage", "isLastPage"]),
    ...mapState("app", ["topBannerHeight"]),
    items() {
      if (this.banners.length !== 2) {
        return this.hits;
      }

      let hits = [...this.hits];
      const bannerDiff = this.isMobile ? [9, 9] : [11, 7];

      for (
        let i = 6, bannerIndex = 0, totalItems = this.hits.length;
        i < totalItems;
        i += bannerDiff[bannerIndex],
          totalItems++, // Increase the toatalItems length to include the inserted banners
          bannerIndex = (bannerIndex + 1) % 2
      ) {
        hits.splice(i, 0, {
          id: `__banner__${i}`,
          ...this.banners[bannerIndex],
        });
      }

      return hits;
    },
    totalResults() {
      return this.currentPageResults?.pagination.totalResults ?? 0;
    },
    pagination() {
      return `Showing ${this.hits.length} of ${this.totalResults} products`;
    },
    filterSummary() {
      return this.currentPageResults?.filterSummary ?? [];
    },

    // check if cache key matches current URL (SSR-safe + normalized)
    usingCache() {
      return this.restoredFromCache === true;
    },
  },

  watch: {
    "$route.query": {
      handler(newQuery, oldQuery) {
        if (this.usingCache) {
          return; // skip scroll when restoring from cache
        }
        const currentPage =
          this.currentPageResults?.pagination?.currentPage ?? 1;
        const oldQueryObj = oldQuery || {};
        const newQueryObj = newQuery || {};
        const isSortChanged =
          Object.keys(newQueryObj).some(
            key =>
              key.startsWith("sort.") && newQueryObj[key] !== oldQueryObj[key]
          ) ||
          Object.keys(oldQueryObj).some(
            key =>
              key.startsWith("sort.") && newQueryObj[key] !== oldQueryObj[key]
          );

        // if not first page AND sort has not changed OR query changed then no scroll
        if (
          (currentPage !== 1 && !isSortChanged) ||
          newQueryObj.query !== oldQueryObj.query
        ) {
          return;
        }

        this.$nextTick(() => {
          // current result page is the first page
          this.scrollToBack(this.$refs.productContainer?.firstChild);
        });
      },
    },
    loading: {
      handler(isLoading) {
        if (isLoading || this.isPageLoaded) {
          return;
        }
        this.$nextTick(() => {
          this.triggerGtmEvent(this.hits);
        });
      },
      immediate: true,
    },
    hits: [
      function scrollOnItemsChange(newItems, oldItems) {
        if (this.usingCache) {
          return; // skip scroll when restoring from cache
        }
        const currentPage =
          this.currentPageResults?.pagination?.currentPage ?? 1;
        if (currentPage === 1) {
          return;
        }

        this.$nextTick(() => {
          // run in next tick so that all product tiles are rendered
          const productTiles = this.$refs.productTile ?? [];
          this.scrollToBack(productTiles[oldItems.length]?.$el);
        });
      },
      {
        // Handler to trigger GTM Event
        handler(newItems, oldItems = []) {
          if (!this.isPageLoaded) {
            return;
          }
          const differenceList = difference(newItems, oldItems);
          if (differenceList.length !== 0) {
            this.triggerGtmEvent(differenceList);
          }
        },
      },
    ],

    restoreScrollData(val) {
      if (!val) {
        return;
      }

      let parsed;
      try {
        parsed = typeof val === "string" ? JSON.parse(val) : val;
      } catch (e) {
        this.restoreScrollData = null;
        return;
      }

      const { productId, route } = parsed;
      if (route !== this.$route.fullPath) {
        this.restoreScrollData = null;
        return;
      }

      this.$nextTick(() => {
        setTimeout(() => {
          const tiles = this.$refs.productTile ?? [];
          const productTile = tiles.find(
            tile => tile.product?.id === productId
          );

          if (productTile?.$el) {
            productTile.$el.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            sessionStorage.removeItem(this.restoreScrollKey);
            this.restoreScrollData = null;
          }
        }, 100);
      });
    },
  },

  mounted() {
    // Force scroll to top on browser reload
    if (typeof window !== "undefined") {
      const isReload =
        performance?.navigation?.type === performance.navigation.TYPE_RELOAD ||
        performance?.getEntriesByType?.("navigation")?.[0]?.type === "reload";

      if (isReload) {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      }
    }

    this.isPageLoaded = true;

    if (typeof window !== "undefined") {
      this.restoreScrollKey = `plp-scroll:${this.$route.fullPath}`;
      const data = sessionStorage.getItem(this.restoreScrollKey);
      this.restoreScrollData = data ? JSON.parse(data) : null;
    }
  },

  methods: {
    getProduct(item) {
      return {
        ...item,
        variants: item.variants.map(variant => ({
          ...variant,
          variantsCount: item.variants_count,
          compareAtPrice: variant.compare_at_price,
          productId: variant.product_id,
          selectedOptions: variant.selected_options.reduce(
            (acc, { name, value }) => ({ ...acc, [name]: { name, value } }),
            {}
          ),
        })),
      };
    },
    getSelectedVariant(item) {
      return this.getProduct(item).variants[0];
    },
    scrollToTop(top) {
      globalThis?.scrollTo({ top, left: 0, behavior: "smooth" });
    },
    scrollToBack(firstElement) {
      if (firstElement == null) {
        return;
      }

      const { top } = firstElement?.getBoundingClientRect?.() ?? { top: 0 };
      const offset = document.documentElement.scrollTop;
      this.scrollToTop(top + offset - this.topBarHeight - this.topBannerHeight);
    },
    triggerGtmEvent(items) {
      this.$gtm.gtagEvent("view_item_list", {
        item_list_id: this.uid,
        item_list_name: this.uid,
        items: items.map((item, index) => {
          const selectedVariant = this.getSelectedVariant(item);

          return {
            item_id: item.id,
            item_sku: selectedVariant.sku,
            item_name: item.title,
            currency: "AUD",
            discount:
              selectedVariant?.compare_at_price && selectedVariant?.price
                ? Number(selectedVariant?.compare_at_price) -
                  Number(selectedVariant?.price)
                : 0,
            index: index + 1,
            item_brand: item.metafields?.brand_list ?? "",
            item_category: item.metafields?.product_category ?? "",
            item_list_id: this.uid,
            item_list_name: this.uid,
            item_variant: selectedVariant.title,
            price: selectedVariant.price,
            quantity: 1,
          };
        }),
      });
    },
  },
};
